import {
  Button,
  Code,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useState } from 'react';

import {
  borrowingBasePipeline as bbpl,
  consts as commonConsts,
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  DeliverableArtifactGQLType,
  useCreateDeliverableArtifactInstanceMutation,
  useEvaluatePortfolioCovenantsMutation,
  useGetCurrentCreditAgreementFullArtifactQuery,
  useGetOrgProvidedInputByKeyQuery,
  useProduceReportingGenerateMessageMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';
import { CsvTape, CsvTapeState } from 'types/manualDataTapeUpload';

import {
  GetDeliverableArtifactsByContainerIdDocumentGQLType,
  GetDeliverableContainerByIdDocumentGQLType,
} from '../../requests/deliverables.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { getTitleCaseExternalFilename } from 'packages/manualDataTapeUploads/ui/DataTapeContainer';
import { UploadCashBalanceForm } from 'packages/manualDataTapeUploads/ui/UploadCashBalanceForm';
import { UploadDataTapeForm } from 'packages/manualDataTapeUploads/ui/UploadDataTapeForm';
import {
  ValueCreateForm,
  ValueCreateModal,
} from 'packages/orgProvidedInputs/ui/ValueCreateModal';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type ErrorModalProps = {
  text: string;
  isOpen: boolean;
  onClose: () => void;
};
export const ErrorModal = ({ text, isOpen, onClose }: ErrorModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="xl">Contact Finley Support.</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody mb="12px">
          <Text>{text}</Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type DataTapeModalProps = {
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: CsvTapeState;
  onSubmitDataTape: (
    file: File,
    tape: CsvTape,
    uploadReportingDate: Util.FullDateStr,
  ) => Promise<void>;
};
const DataTapeModal = ({
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
}: DataTapeModalProps) => {
  // get the minimum date you can upload for using reporting date + reporting period
  const { start: minDateStr, end: maxDateStr } = commonDate.getSearchRange({
    reportingDate: artifact.reportingDate,
    reportingPeriod: JSON.parse(artifact.reportingPeriod),
  });

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const maxCurrentDate = commonDate.fullDateStrToDate(
    commonDate.addBusinessDays(
      commonDate.dateToFullDateString(new Date(), clientTimeZone),
      commonConsts.maxFutureBusinessDaysReportingDate,
    ),
  );
  const maxDate = commonDate.fullDateStrToDate(maxDateStr);
  // datePickerMaxDate is the minimum of `maxCurrentDate` and `maxDate`
  const datePickerMaxDate = maxCurrentDate < maxDate ? maxCurrentDate : maxDate;
  const datePickerMinDate = commonDate.fullDateStrToDate(minDateStr);

  const reportingDateAsDate = commonDate.fullDateStrToDate(
    artifact.reportingDate,
  );
  const [
    uploadReportingDate,
    setUploadReportingDate,
  ] = useState<Util.FullDateStr>(
    reportingDateAsDate <= datePickerMaxDate &&
      reportingDateAsDate >= datePickerMinDate
      ? artifact.reportingDate
      : minDateStr,
  );

  // if minDate > maxDate then we show an error modal & do not proceed with upload
  if (!bbpl.isReportDateLessThanMaxFutureBusinessDays(minDateStr)) {
    const daysToSubtract = -1 * commonConsts.maxFutureBusinessDaysReportingDate;
    const validDate = commonDate.addBusinessDays(minDateStr, daysToSubtract);
    return (
      <ErrorModal
        text={`You cannot upload this file until ${commonDate.fullDateStrToHumanDateString(
          validDate,
        )}.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  if (!dataTape) {
    // Means either loanProductSlug or bbplArtifactSlug does not exist in tape builder
    return (
      <ErrorModal
        text="Missing data tape. Contact Finley Support."
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="2xl">
          {`Upload ${getTitleCaseExternalFilename(dataTape.externalFilename)}`}
        </ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Flex mb="10px" alignItems="baseline">
            <Text mr="25px" color="gray.800">
              Select reporting date for file:
            </Text>
            <SimpleDatePicker
              tz={clientTimeZone}
              minDate={datePickerMinDate}
              maxDate={datePickerMaxDate}
              selectedDate={commonDate.fullDateStrToDate(uploadReportingDate)}
              showTimeSelect={false}
              setSelectedDate={(date: Date) => {
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                setUploadReportingDate(fullDateStr);
              }}
              width="150px"
            />
          </Flex>
          {!!uploadReportingDate && (
            <UploadDataTapeForm
              dataTape={dataTape}
              allowedExtensions={[dataTape.fileType]}
              onClose={onClose}
              handleFile={(file) => {
                onSubmitDataTape(file, dataTape, uploadReportingDate);
              }}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type CashBalancesFeedModalProps = {
  creditFacilitySlug: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
};
const CashBalancesFeedModal = ({
  creditFacilitySlug,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
}: CashBalancesFeedModalProps) => {
  const toast = useToast();
  finleyAssert(
    artifact.config.__typename === 'CashBalancesFeedConfig',
    `Deliverables: Cannot evaluate report for deliverable artifact type ${artifact.config.artifactType}. Must be cashBalancesFeed type.`,
  );

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
        <ModalHeader fontSize="2xl">Upload Cash Balance</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Text fontWeight="bold" mb="1rem" fontSize="xl">
            {commonUtils.toTitleCase(creditFacilitySlug)}
          </Text>
          <UploadCashBalanceForm
            organizationId={organizationId}
            creditFacilityId={creditFacilityId}
            reportingDate={artifact.reportingDate}
            accept={{
              'image/png': ['.png'],
            }}
            onClose={onClose}
            onSubmit={async (artifactInstanceId) => {
              // ******
              // deliverables only keeps track of inProgress records that are instantiated by the Deliverables UI
              // i.e. if cash balance form is submitted elsewhere, deliverables does not create a corresponding inProgress record
              // (it will just look for an completed record when BBPL eventually writes to `cash_response_tapes`)
              // ******
              finleyAssert(
                artifactInstanceId,
                '[Deliverables Cash Balances Feed Upload]: Artifact Instance Id for BBPL Initializing Record must exist as no file version is provided.',
              );
              await createDeliverableArtifactInstanceMutation({
                variables: {
                  deliverableArtifactId: artifact.id,
                  active: true,
                  status: 'inProgress',
                  rawDatabase: 'pg',
                  rawNamespace: 'borrowing_base_pipeline_artifact_instances',
                  rawIdentifier: commonUtils.generatePgIdentifier({
                    column: 'id',
                    id: artifactInstanceId,
                  }),
                },
              });
            }}
            onLoading={() => {}}
            onSuccess={() => {}}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type MonthlyServicerReportModalProps = {
  organizationId: string;
  creditFacilityId: string;
  creditAgreementId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: (instanceId: string) => void; // pass in the instance id of the artifact instance that was generated
};
const MonthlyServicerReportModal = ({
  organizationId,
  creditFacilityId,
  creditAgreementId,
  artifact,
  isOpen,
  onClose,
  onGenerated,
}: MonthlyServicerReportModalProps) => {
  finleyAssert(
    artifact.config.__typename === 'MonthlyServicerReportConfig',
    `Deliverables: Cannot evaluate report for deliverable artifact type ${artifact.config.artifactType}. Must be monthlyServicerReport type.`,
  );

  const toast = useToast();
  const reportName = artifact.config.reportName;

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
    onCompleted(data) {
      if (onGenerated) {
        onGenerated(data.deliverableArtifactInstance.id);
      }
    },
  });

  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'network-only',
    async onCompleted(data) {
      try {
        // create corresponding deliverable artifact instance
        await createDeliverableArtifactInstanceMutation({
          variables: {
            deliverableArtifactId: artifact.id,
            active: true,
            status: 'inProgress',
            rawDatabase: 'pg',
            rawNamespace: 'reporting_generate_worklease_status',
            rawIdentifier: commonUtils.generatePgIdentifier({
              column: 'id',
              id: data.result.recordId,
            }),
          },
        });
        toast({
          description:
            'Successfully produced message to generate report. Please check your email in a few minutes.',
          duration: 3000,
          status: 'success',
          position: 'top',
        });
      } catch (err) {
        toast({
          status: 'error',
          position: 'top',
          duration: 5000,
          isClosable: true,
          title:
            commonTypeguards.isFinleyErrorType(err) ||
            commonTypeguards.isErrorType(err)
              ? err.message
              : 'Failed to produce message to generate report. Please contact Finley support.',
        });
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Generate Monthly Servicer Report</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Stack spacing="20px">
            <Text fontWeight="semibold" mb="1rem" fontSize="md">
              Are you sure you want to generate an MSR report?
            </Text>
            <Flex padding-top="5px" justifyContent="center" pb={3}>
              <Button
                mr="15px"
                variant="solid"
                colorScheme="aurora"
                _focus={{ boxShadow: 'none' }}
                onClick={() => {
                  produceGenerateMessage({
                    variables: {
                      worklease: {
                        initiatingOrganizationId: organizationId,
                        creditFacilityId,
                        creditAgreementId,
                        reportName,
                        reportingDate: artifact.reportingDate,
                        mode: 'Live',
                        reportTemplateFileType: 'xlsx',
                        reportConfigType: 'monthlyServicerReport',
                        config: null,
                      },
                    },
                  });
                }}
              >
                Generate
              </Button>
              <Button
                color="aurora"
                _focus={{ boxShadow: 'none' }}
                onClick={() => onClose()}
              >
                Cancel
              </Button>
            </Flex>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type PortfolioCovenantModalProps = {
  creditFacilityId: string;
  creditAgreementId: string;
  portCovs: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  onGenerated?: (instanceId: string) => void; // pass in the instance id of the artifact instance that was generated
};
const PortfolioCovenantModal = ({
  creditFacilityId,
  creditAgreementId,
  portCovs,
  artifact,
  isOpen,
  onClose,
  onGenerated,
}: PortfolioCovenantModalProps) => {
  finleyAssert(
    artifact.config.__typename === 'PortfolioCovenantConfig',
    `Deliverables: Cannot evaluate port cov for deliverable artifact type ${artifact.config.artifactType}. Must be portfolioCovenant type.`,
  );

  const toast = useToast();
  const covenantKey = artifact.config.covenantKey;
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
    onCompleted(data) {
      if (onGenerated) {
        onGenerated(data.deliverableArtifactInstance.id);
      }
    },
  });

  const [evaluatePortfolioCovenants] = useEvaluatePortfolioCovenantsMutation({
    fetchPolicy: 'network-only',
    async onCompleted(data) {
      try {
        finleyAssert(
          data.evaluatePortfolioCovenants.portfolioCovenantIds.length === 1,
          '[Deliverables] Exactly one portfolio covenant should be evaluated at a time.',
        );
        const portfolioCovenantId =
          data.evaluatePortfolioCovenants.portfolioCovenantIds[0];
        // create corresponding deliverable artifact instance
        await createDeliverableArtifactInstanceMutation({
          variables: {
            deliverableArtifactId: artifact.id,
            active: true,
            status: 'inProgress',
            rawDatabase: 'pg',
            rawNamespace: 'portfolio_covenants',
            rawIdentifier: commonUtils.generatePgIdentifier({
              column: 'id',
              id: portfolioCovenantId,
            }),
          },
        });

        toast({
          title:
            'Initiated portfolio covenants evaluation.  Please check your email in a few minutes.',
          duration: 3000,
          status: 'success',
          position: 'top',
        });
      } catch (e) {
        toast({
          status: 'error',
          position: 'top',
          duration: 5000,
          isClosable: true,
          title:
            commonTypeguards.isFinleyErrorType(e) && e.externalMessage
              ? e.externalMessage
              : 'An unexpected error occurred. Please contact Finley support.',
        });
      }
    },
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const onSubmit = useCallback(
    async (props: { triggerDate: Util.FullDateStr }) => {
      await evaluatePortfolioCovenants({
        variables: {
          evaluatePortfolioCovenantsRequest: {
            covenantKeys: [covenantKey],
            creditAgreementId,
            creditFacilityId,
            triggerDate: props.triggerDate,
            mode: 'RESULT',
            // result mode means we expect to get back exactly one portfolio covenant id corresponding to the record for this covenantKey
            // (e.g. evaluatePortfolioCovenants should return `portfolioCovenantIds.length === 1`)
          },
        },
      });
      onClose();
    },
    [
      onClose,
      evaluatePortfolioCovenants,
      covenantKey,
      creditAgreementId,
      creditFacilityId,
    ],
  );

  const portCov = portCovs.find((pc) => pc.covenantKey === covenantKey);
  if (!portCov) {
    return (
      <ErrorModal
        text={`Portfolio Covenant with covenant key ${covenantKey} is missing in current credit agreement.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const reportingDateField = portCov._config_?.intermediateFields.find(
    (f) => f.field === 'portfolioCovenantReportingDate',
  );
  if (!reportingDateField) {
    return (
      <ErrorModal
        text={`Portfolio Covenant with covenant key ${covenantKey} is missing 'portfolioCovenantReportingDate' config intermediate field.`}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Evaluate Portfolio Covenants</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody>
          <Formik
            initialValues={{
              triggerDate: commonDate.dateToFullDateString(
                new Date(),
                clientTimeZone,
              ),
            }}
            onSubmit={(props) => {
              onSubmit(props);
            }}
          >
            {(formProps: FormikProps<any>) => (
              <Form onSubmit={formProps.handleSubmit}>
                <Stack spacing="20px">
                  <Flex alignItems="center">
                    <Text minW="25%">Reporting Date Formula:</Text>
                    <Spacer />
                    <Code>{reportingDateField.config.formula}</Code>
                  </Flex>

                  <Divider />

                  <Text fontWeight="semibold">
                    {`Pass in a triggerDate below such that reporting date formula evaluates to ${commonDate.fullDateStrToHumanDateString(
                      artifact.reportingDate,
                    )}.`}
                  </Text>
                  <Flex alignItems="center" width="30%">
                    <SimpleDatePicker
                      selectedDate={commonDate.fullDateStrToDate(
                        formProps.values.triggerDate,
                      )}
                      setSelectedDate={(date: Date) => {
                        const fullDateStr = commonDate.dateToFullDateString(
                          date,
                          clientTimeZone,
                        );
                        formProps.setFieldValue('triggerDate', fullDateStr);
                      }}
                    />
                  </Flex>

                  <Flex padding-top="5px" justifyContent="flex-end" pb={3}>
                    <Button
                      type="submit"
                      mr="15px"
                      variant="solid"
                      colorScheme="aurora"
                      _focus={{ boxShadow: 'none' }}
                    >
                      Evaluate
                    </Button>
                    <Button
                      color="aurora"
                      _focus={{ boxShadow: 'none' }}
                      onClick={() => onClose()}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </Stack>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type OrgProvidedInputModalProps = {
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: CsvTapeState;
  onSubmitDataTape: (
    file: File,
    tape: CsvTape,
    uploadReportingDate: Util.FullDateStr,
  ) => Promise<void>;
};
const OrgProvidedInputModal = ({
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
}: OrgProvidedInputModalProps) => {
  finleyAssert(
    artifact.config.__typename === 'OrgProvidedInputConfig',
    `Deliverables: Cannot upload Org Provided Value for deliverable artifact type ${artifact.config.artifactType}. Must be orgProvidedInput type.`,
  );

  const bgColor = useColorModeValue(colors.aurora['400'], 'white');
  const bgTextColor = useColorModeValue('white', 'black');

  const { data: orgProvidedInputsData } = useGetOrgProvidedInputByKeyQuery({
    variables: {
      organizationId,
      key: artifact.config.key,
    },
  });
  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  if (!orgProvidedInputsData) {
    return <Skeleton />;
  }

  const orgProvidedInput = orgProvidedInputsData.getOrgProvidedInputByKey;

  const onSubmitPrimitive = async (orgProvidedValueId: string) => {
    // create corresponding deliverable artifact instance
    await createDeliverableArtifactInstanceMutation({
      variables: {
        deliverableArtifactId: artifact.id,
        active: true,
        status: 'success',
        completedDatabase: 'pg',
        completedNamespace: 'org_provided_values',
        completedIdentifier: commonUtils.generatePgIdentifier({
          column: 'id',
          id: orgProvidedValueId,
        }),
      },
    });

    onClose();
  };

  // if data tape doesn't exist, only support primitive upload
  if (!dataTape) {
    return (
      <ValueCreateModal
        isOpen={isOpen}
        onCancel={onClose}
        onSubmit={onSubmitPrimitive}
        orgProvidedInputs={[orgProvidedInput]}
        reportingDate={artifact.reportingDate}
      />
    );
  }

  // if data tape exists, allow for EITHER csv upload OR primitive upload
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Create Org Provided Value (${getTitleCaseExternalFilename(
          dataTape.externalFilename,
        )})`}</ModalHeader>
        <ModalCloseButton _focus={{ boxShadow: 'none' }} />
        <ModalBody width="100%">
          <Tabs isFitted variant="unstyled">
            <TabList>
              <Tab
                marginTop={0}
                marginRight={0}
                height="40px"
                borderWidth="thin"
                borderRadius="8px 0px 0px 8px"
                _selected={{
                  color: bgTextColor,
                  bg: bgColor,
                }}
                _focus={{ boxShadow: 'none' }}
              >
                Value
              </Tab>
              <Tab
                marginTop={0}
                marginRight={0}
                height="40px"
                borderWidth="thin"
                borderRadius="0px 8px 8px 0px"
                _selected={{
                  color: bgTextColor,
                  bg: bgColor,
                }}
                _focus={{ boxShadow: 'none' }}
              >
                CSV File
              </Tab>
            </TabList>

            <TabPanels>
              {/* Value */}
              <TabPanel px="0px">
                <ValueCreateForm
                  orgProvidedInputs={[orgProvidedInput]}
                  reportingDate={artifact.reportingDate}
                  onCancel={onClose}
                  onSubmit={onSubmitPrimitive}
                />
              </TabPanel>

              {/* CSV */}
              <TabPanel px="0px">
                <UploadDataTapeForm
                  dataTape={dataTape}
                  allowedExtensions={[dataTape.fileType]}
                  onClose={onClose}
                  handleFile={(file) => {
                    onSubmitDataTape(file, dataTape, artifact.reportingDate);
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const GenerateOutput = ({
  creditFacilityId,
  artifact,
  isOpen,
  onGenerated,
  onClose,
}: {
  creditFacilityId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onGenerated: (instanceId: string) => void; // pass in the instance id of the artifact instance that was generated
  onClose: () => void;
}) => {
  const toast = useToast();

  const { user } = useUserAuthOrThrow();

  const {
    loading: creditAgreementLoading,
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  if (creditAgreementLoading) {
    return <Skeleton />;
  }

  const creditAgreement = parseCreditAgreementFullArtifact(
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
  );

  if (!creditAgreement) {
    return (
      <ErrorModal
        text="A current Credit Agreement must exist to upload Deliverables."
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const { id: creditAgreementId, portfolioCovenants } = creditAgreement;

  switch (artifact.config.artifactType) {
    case 'monthlyServicerReportXlsx':
      return (
        <MonthlyServicerReportModal
          organizationId={user.organizationId}
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          onGenerated={onGenerated}
        />
      );
    case 'portfolioCovenant':
      return (
        <PortfolioCovenantModal
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          portCovs={portfolioCovenants}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          onGenerated={onGenerated}
        />
      );
    // @jendevelops MSR-TODO
    case 'monthlyStatement':
    case 'orgProvidedInput':
    case 'loanTape':
    case 'repaymentTape':
    case 'supplementalTape':
    case 'cashBalancesFeed':
      throw new Error(
        'Not implemented yet - at this moment we only support generating MSRs and portfolio covenants',
      );
    default:
      return finleyAssertUnreachable(artifact.config.artifactType);
  }
};

export const Upload = ({
  creditFacilitySlug,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
}: {
  creditFacilitySlug: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  // below two params are only for manual uploader file uploads (loan tape, repayment tape, and supplemental tape)
  // will be ignored for other deliverable artifact types
  dataTape?: CsvTapeState;
  onSubmitDataTape: (
    file: File,
    tape: CsvTape,
    uploadReportingDate: Util.FullDateStr,
  ) => Promise<void>;
}) => {
  const toast = useToast();

  const {
    loading: creditAgreementLoading,
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  if (creditAgreementLoading) {
    return <Skeleton />;
  }

  const creditAgreement = parseCreditAgreementFullArtifact(
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement,
  );

  if (!creditAgreement) {
    return (
      <ErrorModal
        text="A current Credit Agreement must exist to upload Deliverables."
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const { id: creditAgreementId, portfolioCovenants } = creditAgreement;

  switch (artifact.config.artifactType) {
    case 'loanTape':
    case 'repaymentTape':
    case 'supplementalTape':
      return (
        <DataTapeModal
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          dataTape={dataTape}
          onSubmitDataTape={onSubmitDataTape}
        />
      );
    case 'cashBalancesFeed':
      return (
        <CashBalancesFeedModal
          creditFacilitySlug={creditFacilitySlug}
          creditFacilityId={creditFacilityId}
          organizationId={organizationId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'monthlyServicerReportXlsx':
      return (
        <MonthlyServicerReportModal
          organizationId={organizationId}
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'portfolioCovenant':
      return (
        <PortfolioCovenantModal
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreementId}
          portCovs={portfolioCovenants}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
        />
      );
    case 'orgProvidedInput':
      return (
        <OrgProvidedInputModal
          organizationId={organizationId}
          artifact={artifact}
          isOpen={isOpen}
          onClose={onClose}
          dataTape={dataTape}
          onSubmitDataTape={onSubmitDataTape}
        />
      );
    case 'monthlyStatement':
      return null;
    default:
      return finleyAssertUnreachable(artifact.config.artifactType);
  }
};
